import React, { useCallback } from 'react';
import Form from '@m4b-design/form';
import { SelectCascader } from '@oec-seller/sdk/m4b-seller-material';
import Cascader from '@m4b-design/cascader';
import type { CascaderItem } from '@oec-seller/sdk/m4b-seller-material';
import { MAIN_TOPIC_MAX_LEVEL, CreateFormField } from '@/containers/create/constants';
import { useTranslation } from '@/i18n';
import cx from 'classnames';
import { SELLER_EMAIL_WEBFORM } from '@/constants/logger/webform';
import { useTeaReport } from '@/hooks/useTeaReport';
import { acquireCluster } from '@/utils';

interface TopicItemProps {
  generalReasons: CascaderItem[];
  flatReasons: CascaderItem[];
  disabled: boolean;
  className?: string;
}

const TopicItem: React.FC<TopicItemProps> = props => {
  const { generalReasons, flatReasons, disabled, className } = props;
  const { t } = useTranslation('i18n_ecom_shop_email');
  const trackEvent = useTeaReport();
  const isTTP = acquireCluster() === 'ttp';

  const validator = (values: string[] | undefined, cb: (error: string) => void): void => {
    const options =
      values?.map(value => {
        const option = flatReasons.find(item => item.value === value);
        return option ?? { label: value, value, children: undefined };
      }) ?? [];

    const lastOption = options[options.length - 1];
    if (!options.length || (options.length < MAIN_TOPIC_MAX_LEVEL && lastOption?.children?.length)) {
      cb(t('contact_us_form_error_option', 'Please select an option'));
    }
  };

  const logWebformClickTopic = useCallback(
    (options?: string[] | undefined): void => {
      if (options?.length === 1) {
        trackEvent.click(SELLER_EMAIL_WEBFORM.CLICK_TOPIC_LEVEL_1, {
          targetValue: flatReasons.find(item => item.value === options[0])?.label
        });
      } else if (options?.length === 2) {
        trackEvent.click(SELLER_EMAIL_WEBFORM.CLICK_TOPIC_LEVEL_2, {
          targetValue: flatReasons.find(item => item.value === options[1])?.label
        });
      }
    },
    [flatReasons, trackEvent]
  );

  return (
    <div className={cx(className, 'w-full')}>
      <div className="mb-8 text-neutral-text1 font-semibold">{t('contact_us_form_topic', 'Topic')}</div>
      <Form.Item
        field={CreateFormField.Topic}
        rules={[{ validator: validator }]}
        validateTrigger={['onSubmit', 'onBlur']}
        disabled={disabled}
      >
        {isTTP ? (
          <Cascader
            style={{ width: '616px' }}
            options={generalReasons}
            placeholder={t('contact_us_form_topic_placeholder', 'Please select a topic') as string}
          />
        ) : (
          <SelectCascader
            maxLevel={Number.MAX_VALUE}
            options={generalReasons}
            getPopupContainer={(node: HTMLElement): HTMLElement => node}
            placeholder={[t('contact_us_form_topic_placeholder', 'Please select a topic')]}
            onChange={logWebformClickTopic}
          />
        )}
      </Form.Item>
    </div>
  );
};

export default TopicItem;
